import {InputHTMLAttributes, ReactElement, useRef} from "react";
import "./AutofillTextInput.scss";

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange"> {
    onValueChange: (value: string) => void;
}

/**
 * Text input that reliably syncs browser autofill into React state.
 *
 * Chrome sets the DOM value on autofill but does not always fire React's
 * onChange (notably for cross-field card autofill), so a plain controlled input
 * is left stale — the field visibly shows the value while the form still treats
 * it as empty (the "delete a letter to make it register" bug). We detect the
 * fill via the `:-webkit-autofill` CSS animation (AutofillTextInput.scss) and
 * push the real DOM value up immediately, so validation/enable state stays in
 * sync without the user having to edit the field.
 */
export default function AutofillTextInput({onValueChange, className, ...rest}: Props): ReactElement {
    const ref = useRef<HTMLInputElement>(null);

    return (
        <input
            {...rest}
            ref={ref}
            className={["autofill-sync-input", className].filter(Boolean).join(" ")}
            onChange={(e) => onValueChange(e.target.value)}
            onAnimationStart={(e) => {
                if (e.animationName === "onAutoFillStart" && ref.current) {
                    onValueChange(ref.current.value);
                }
            }}
        />
    );
}
